import type {
  ChamferFeatureDefinition,
  EdgeFilletMode,
  ChamferType,
  FilletFeatureDefinition,
  FilletType,
} from '@puppycad/cad-core';
import {
  convertMmToM,
  DEFAULT_CHAMFER_DISTANCE_M,
} from '@puppycad/cad-core';
import {
  createDefaultVariableRadiusPoints as createDefaultVariableRadiusEndpointPoints,
  DEFAULT_VARIABLE_FILLET_INSTANCE_COUNT,
} from './variable-radius';

const SUPPORTED_FILLET_TYPES = new Set<FilletType>(['constant', 'variable', 'face']);
const SUPPORTED_CHAMFER_TYPES = new Set<ChamferType>(['angle-distance', 'distance-distance', 'vertex', 'offset-face', 'face-face']);

const DEFAULT_FILLET_RADIUS = convertMmToM(10);
/** 与 cad-core 归一化层共享的倒角默认距离（米）。 */
export const DEFAULT_CHAMFER_DISTANCE = DEFAULT_CHAMFER_DISTANCE_M;
const DEFAULT_CHAMFER_ANGLE_DEG = 45;

export function isSupportedFilletType(type: FilletType): boolean {
  return SUPPORTED_FILLET_TYPES.has(type);
}

export function isSupportedChamferType(type: ChamferType): boolean {
  return SUPPORTED_CHAMFER_TYPES.has(type);
}

type BlendSelectionModeItem = {
  kind: 'edge' | 'face' | 'vertex';
};

function hasFaceItems(items: BlendSelectionModeItem[]): boolean {
  return items.some((item) => item.kind === 'face');
}

/**
 * 解析固定大小圆角的边圆角模式。
 *
 * @param edgeFilletMode 当前请求的边圆角模式。
 * @param items 当前主选择项目。
 * @returns 允许提交到 solver 的边圆角模式。
 */
export function resolveConstantFilletEdgeMode(
  edgeFilletMode: EdgeFilletMode | undefined,
  items: BlendSelectionModeItem[],
): EdgeFilletMode {
  return hasFaceItems(items) ? 'symmetric' : (edgeFilletMode ?? 'symmetric');
}

/**
 * 规范化固定大小圆角定义。
 *
 * @param definition 原始圆角定义。
 * @param items 当前主选择项目。
 * @returns 与选择类型一致的圆角定义。
 */
export function normalizeConstantFilletDefinition(
  definition: FilletFeatureDefinition,
  items: BlendSelectionModeItem[],
): FilletFeatureDefinition {
  if (definition.type !== 'constant') {
    return definition;
  }
  const nextMode = resolveConstantFilletEdgeMode(definition.edgeFilletMode, items);
  if (nextMode === (definition.edgeFilletMode ?? 'symmetric')) {
    return definition;
  }
  return {
    ...definition,
    edgeFilletMode: nextMode,
    /**
     * 非对称翻转只在纯边非对称子模式里有效。
     * 选面时必须同步回收，避免提交一个不可见但影响 solver 的状态。
     */
    asymmetricRadiiFlipped: nextMode === 'asymmetric'
      ? definition.asymmetricRadiiFlipped === true
      : false,
  };
}

export function resolveFilletSelectionMode(
  type: FilletType,
  options: {
    edgeFilletMode?: EdgeFilletMode;
    items?: BlendSelectionModeItem[];
  } = {},
): 'face-edge' | 'edge' | 'face' | 'vertex' {
  if (type === 'constant') {
    return resolveConstantFilletEdgeMode(options.edgeFilletMode, options.items ?? []) === 'asymmetric'
      ? 'edge'
      : 'face-edge';
  }
  return type === 'face' ? 'face' : 'edge';
}

export function resolveChamferSelectionMode(type: ChamferType): 'face-edge' | 'edge' | 'face' | 'vertex' {
  if (type === 'face-face') {
    return 'face';
  }
  if (type === 'vertex') {
    return 'vertex';
  }
  if (type === 'offset-face' || type === 'angle-distance' || type === 'distance-distance') {
    return 'face-edge';
  }
  return 'edge';
}

export function createDefaultFilletDefinition(): FilletFeatureDefinition {
  return {
    type: 'constant',
    tangentPropagation: true,
    selectThroughFaces: false,
    keepFeatures: true,
    items: [],
    radius: DEFAULT_FILLET_RADIUS,
    radiusExpression: null,
    radius2Expression: null,
    chordWidth: DEFAULT_FILLET_RADIUS,
    chordWidthExpression: null,
    overflowType: 'default',
    edgeFilletMode: 'symmetric',
    asymmetricRadiiFlipped: false,
    partialEdgeParametersEnabled: false,
    partialEdges: [],
    variablePoints: [],
    variableInstanceCount: DEFAULT_VARIABLE_FILLET_INSTANCE_COUNT,
    variableMode: 'symmetric',
    variableProfile: 'circular',
    variableTransition: 'smooth',
    faceSets: [],
    radius2: DEFAULT_FILLET_RADIUS,
    helpPoint: null,
    faceFilletMode: 'symmetric',
    faceFilletRadiiFlipped: false,
    trimSurfaces: true,
  };
}

export function createDefaultChamferDefinition(): ChamferFeatureDefinition {
  return {
    type: 'angle-distance',
    tangentPropagation: false,
    selectThroughFaces: false,
    keepFeatures: true,
    flipDirection: false,
    items: [],
    distance: DEFAULT_CHAMFER_DISTANCE,
    distanceExpression: null,
    distance2: DEFAULT_CHAMFER_DISTANCE,
    distance2Expression: null,
    width: DEFAULT_CHAMFER_DISTANCE,
    widthExpression: null,
    angleDeg: DEFAULT_CHAMFER_ANGLE_DEG,
    angleExpression: null,
    symmetric: true,
    equalDistance: false,
    vertexDistances: [DEFAULT_CHAMFER_DISTANCE, DEFAULT_CHAMFER_DISTANCE, DEFAULT_CHAMFER_DISTANCE],
    vertexDistanceExpressions: [null, null, null],
    partialEdgeParametersEnabled: false,
    partialEdges: [],
    faceSets: [],
    faceFaceMode: 'symmetric',
    helpPoint: null,
  };
}

export function createDefaultVariableRadiusPoints(
  itemRef: string,
  radius: number | null = null,
  expression: string | null = null,
) {
  return createDefaultVariableRadiusEndpointPoints(itemRef, radius, expression);
}
